import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ClipboardCheck, 
  TrendingUp, 
  Calendar,
  Target,
  Activity,
  Heart,
  Scale,
  Ruler,
  ChevronRight,
  Award,
  CheckCircle,
  Clock,
  FileText,
  Save,
  ArrowLeft
} from "lucide-react";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { examApi, Exam, ExamAnswer } from "@/services/api";
import { toast } from "@/hooks/use-toast";
import Navigation from "./Navigation";
import { useAuth } from "@/contexts/AuthContext";

const AssessmentsPage = () => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const { clientName, isAuthenticated, isLoading } = useAuth();
  
  const [assignedExams, setAssignedExams] = useState<Exam[]>([]);
  const [selectedExam, setSelectedExam] = useState<Exam | null>(null);
  const [isTakingExam, setIsTakingExam] = useState(false);
  const [answers, setAnswers] = useState<ExamAnswer[]>([]);
  const [loading, setLoading] = useState(true);

  // Redirect to auth if not authenticated (only after loading is complete)
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Load assigned exams for the client
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    loadAssignedExams();
  }, [isAuthenticated, clientName, isLoading]);

  // Load specific exam if examId is in URL
  useEffect(() => {
    if (examId) {
      loadSpecificExam(examId);
    }
  }, [examId]);

  const loadAssignedExams = async () => {
    if (!isAuthenticated || !clientName) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      console.log('Loading exams for client:', clientName);
      const exams = await examApi.getClientExamsByName(clientName);
      console.log('Found exams:', exams);
      setAssignedExams(exams);
    } catch (error) {
      console.error('Error loading exams:', error);
      toast({
        title: "Error",
        description: `Failed to load assigned exams: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSpecificExam = async (id: string) => {
    try {
      const exam = await examApi.getExamById(id);
      setSelectedExam(exam);
      setIsTakingExam(true);
      
      // Load existing answers if any
      if (exam.answers && exam.answers.length > 0) {
        setAnswers(exam.answers);
      } else {
        // Initialize empty answers
        const initialAnswers = exam.questions.map(q => ({
          questionId: q._id!,
          answer: "",
          answeredAt: new Date()
        }));
        setAnswers(initialAnswers);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load exam",
        variant: "destructive",
      });
    }
  };

  const handleStartExam = (exam: Exam) => {
    setSelectedExam(exam);
    setIsTakingExam(true);
    
    // Initialize answers
    const initialAnswers = exam.questions.map(q => ({
      questionId: q._id!,
      answer: "",
      answeredAt: new Date()
    }));
    setAnswers(initialAnswers);
    
    // Update URL
    navigate(`/assessments/exam/${exam._id}`);
  };

  const handleBackToExams = () => {
    setSelectedExam(null);
    setIsTakingExam(false);
    setAnswers([]);
    navigate('/assessments');
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers(prev => 
      prev.map(a => 
        a.questionId === questionId 
          ? { ...a, answer, answeredAt: new Date() }
          : a
      )
    );
  };

  const handleSaveAnswers = async () => {
    if (!selectedExam) return;

    try {
      // Since we're using client name, we need to get the client ID from the exam
      const clientId = typeof selectedExam.assignedTo === 'string' 
        ? selectedExam.assignedTo 
        : selectedExam.assignedTo?._id;
        
      if (!clientId) {
        throw new Error('Client ID not found');
      }
      
      await examApi.submitExamAnswers(selectedExam._id!, clientId, { answers });
      
      toast({
        title: "Success",
        description: "Your answers have been saved successfully",
      });
      
      // Reload exams to update status
      await loadAssignedExams();
      
      // Go back to exam list
      handleBackToExams();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save answers",
        variant: "destructive",
      });
    }
  };

  const getExamStatus = (exam: Exam) => {
    return exam.status || "assigned";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "bg-success text-success-foreground";
      case "in_progress": return "bg-warning text-warning-foreground";
      case "overdue": return "bg-destructive text-destructive-foreground";
      default: return "bg-secondary text-secondary-foreground";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed": return CheckCircle;
      case "in_progress": return Clock;
      case "overdue": return Clock;
      default: return FileText;
    }
  };

  // Group exams by time intervals and dates
  const groupExamsByTime = (exams: Exam[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const groups: { [key: string]: Exam[] } = {};
    
    exams.forEach(exam => {
      const examDate = new Date(exam.assignedAt || exam.createdAt!);
      const examDay = new Date(examDate.getFullYear(), examDate.getMonth(), examDate.getDate());
      const examHour = examDate.getHours();
      
      let groupKey = '';
      
      // Determine date group
      if (examDay.getTime() === today.getTime()) {
        groupKey = 'Today';
      } else if (examDay.getTime() === yesterday.getTime()) {
        groupKey = 'Yesterday';
      } else if (examDay < yesterday) {
        groupKey = examDay.toLocaleDateString('en-US', { 
          weekday: 'long', 
          month: 'long', 
          day: 'numeric' 
        });
      } else {
        groupKey = 'Future';
      }
      
      // Add hour grouping within each day
      const hourGroup = Math.floor(examHour / 1) * 1; // Group by 1-hour intervals
      const timeKey = `${groupKey} - ${hourGroup.toString().padStart(2, '0')}:00`;
      
      if (!groups[timeKey]) {
        groups[timeKey] = [];
      }
      groups[timeKey].push(exam);
    });
    
    // Sort groups by date and time
    const sortedGroups = Object.entries(groups).sort(([a], [b]) => {
      const aDate = new Date(a.split(' - ')[0] === 'Today' ? today : 
                           a.split(' - ')[0] === 'Yesterday' ? yesterday : 
                           new Date(a.split(' - ')[0]));
      const bDate = new Date(b.split(' - ')[0] === 'Today' ? today : 
                           b.split(' - ')[0] === 'Yesterday' ? yesterday : 
                           new Date(b.split(' - ')[0]));
      
      if (aDate.getTime() !== bDate.getTime()) {
        return bDate.getTime() - aDate.getTime(); // Most recent first
      }
      
      // If same date, sort by hour
      const aHour = parseInt(a.split(' - ')[1]);
      const bHour = parseInt(b.split(' - ')[1]);
      return bHour - aHour;
    });
    
    return sortedGroups;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading your assessments...</div>
        </div>
      </div>
    );
  }

  if (isTakingExam && selectedExam) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <div className="max-w-4xl mx-auto p-4 md:p-8">
          <div className="mb-6">
            <Button 
              variant="outline" 
              onClick={handleBackToExams}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Assessments
            </Button>
            
            <h1 className="text-3xl font-bold text-foreground mb-2">{selectedExam.title}</h1>
            <p className="text-muted-foreground mb-6">
              {selectedExam.description || "Please answer all questions below"}
            </p>
          </div>

          <Card className="card-wellness">
            <CardContent className="p-6">
              <div className="space-y-6">
                {selectedExam.questions.map((question, index) => (
                  <div key={question._id} className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <span className="font-semibold text-sm bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-base mb-3">{question.question}</p>
                        <Textarea 
                          placeholder="Write your answer here..."
                          value={answers.find(a => a.questionId === question._id)?.answer || ""}
                          onChange={(e) => handleAnswerChange(question._id!, e.target.value)}
                          className="min-h-[120px] w-full"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button variant="outline" onClick={handleBackToExams}>
                  Cancel
                </Button>
                <Button onClick={handleSaveAnswers}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Answers
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2 fade-in">My Assessments</h1>
          <p className="text-muted-foreground">Complete the assessments assigned by your doctor</p>
          
          {!isAuthenticated && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Please log in to view your assigned exams.
              </p>
                  </div>
          )}
        </div>

        <Tabs defaultValue="assigned" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="assigned">Assigned Exams</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>
          
          <TabsContent value="assigned" className="mt-6">
            <div className="space-y-8">
              {(() => {
                const nonCompletedExams = assignedExams.filter(exam => getExamStatus(exam) !== "completed");
                const groupedExams = groupExamsByTime(nonCompletedExams);
                
                return groupedExams.map(([timeGroup, exams]) => (
                  <div key={timeGroup} className="space-y-4">
                    {/* Time Group Header */}
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 border-t border-border"></div>
                      <div className="px-4 py-2 bg-muted rounded-lg">
                        <span className="text-sm font-medium text-muted-foreground">
                          {timeGroup}
                        </span>
                      </div>
                      <div className="flex-1 border-t border-border"></div>
                    </div>
                    
                    {/* Exams in this time group */}
                    <div className="grid gap-6">
                      {exams.map((exam) => {
                        const status = getExamStatus(exam);
                        const StatusIcon = getStatusIcon(status);
                        
                        return (
                          <Card key={exam._id} className="card-wellness hover:shadow-md transition-shadow">
                            <CardContent className="p-6">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-4 flex-1">
                                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <FileText className="w-6 h-6 text-primary" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-2">
                                      <h3 className="font-semibold text-foreground">{exam.title}</h3>
                                      <Badge className={getStatusColor(status)}>
                                        <StatusIcon className="w-3 h-3 mr-1" />
                                        {status.replace('_', ' ')}
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground mb-3">
                                      {exam.description || `${exam.questions.length} questions to complete`}
                                    </p>
                                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                                      <div className="flex items-center space-x-1">
                                        <Calendar className="w-4 h-4" />
                                        <span>Assigned: {new Date(exam.assignedAt || exam.createdAt!).toLocaleDateString()}</span>
                                      </div>
                                      {exam.estimatedTime && (
                                        <span>Estimated time: {exam.estimatedTime} minutes</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <Button 
                                  onClick={() => handleStartExam(exam)}
                                  disabled={status === "completed"}
                                >
                                  {status === "completed" ? "Completed" : "Start Exam"}
                                  <ChevronRight className="w-4 h-4 ml-2" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
              
              {assignedExams.filter(exam => getExamStatus(exam) !== "completed").length === 0 && (
                <Card className="card-wellness">
                  <CardContent className="p-8 text-center">
                    <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">No Assigned Exams</h3>
                    <p className="text-muted-foreground">Your doctor hasn't assigned any exams yet.</p>
              </CardContent>
            </Card>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="completed" className="mt-6">
            <div className="space-y-8">
              {(() => {
                const completedExams = assignedExams.filter(exam => getExamStatus(exam) === "completed");
                const groupedExams = groupExamsByTime(completedExams);
                
                return groupedExams.map(([timeGroup, exams]) => (
                  <div key={timeGroup} className="space-y-4">
                    {/* Time Group Header */}
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 border-t border-border"></div>
                      <div className="px-4 py-2 bg-muted rounded-lg">
                        <span className="text-sm font-medium text-muted-foreground">
                          {timeGroup}
                        </span>
                      </div>
                      <div className="flex-1 border-t border-border"></div>
                    </div>
                    
                    {/* Exams in this time group */}
                    <div className="grid gap-6">
                      {exams.map((exam) => {
                        return (
                          <Card key={exam._id} className="card-wellness">
                            <CardContent className="p-6">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-4 flex-1">
                                  <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                                    <CheckCircle className="w-6 h-6 text-success" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-2">
                                      <h3 className="font-semibold text-foreground">{exam.title}</h3>
                                      <Badge className="bg-success text-success-foreground">
                                        <CheckCircle className="w-3 h-3 mr-1" />
                                        Completed
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-muted-foreground mb-3">
                                      {exam.submittedAt && (
                                        `Completed on ${new Date(exam.submittedAt).toLocaleDateString()}`
                                      )}
                                    </p>
                                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                                      <span>{exam.questions.length} questions answered</span>
                                      {exam.score && (
                                        <span>Score: {exam.score}%</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <Button 
                                  variant="outline"
                                  onClick={() => handleStartExam(exam)}
                                >
                                  View Answers
                                  <ChevronRight className="w-4 h-4 ml-2" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
              
              {assignedExams.filter(exam => getExamStatus(exam) === "completed").length === 0 && (
                <Card className="card-wellness">
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">No Completed Exams</h3>
                    <p className="text-muted-foreground">Complete your assigned exams to see them here.</p>
              </CardContent>
            </Card>
              )}
                    </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AssessmentsPage;